import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useDocumentTitle from "@/components/ui/common/document-title";
import SalesReportTable from "./table";
import SalesSummary from "./summary/sales-summary";
import SalesCharts from "./charts/sales-charts";
import SalesReportFilters from "./shared/sales-report-filters";
import { useGetSalesReportQuery } from "@/services/transactionApi";
import { formatDate } from "@/lib/helper";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

export default function SalesReport() {
  useDocumentTitle("Sales Report");

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const [activeTab, setActiveTab] = useState<string>("transactions");

  // Filter type that supports both single values and arrays
  type FilterState = {
    start_date?: string;
    end_date?: string;
    income_category_ids?: (string | number)[];
    payment_method_ids?: (string | number)[];
    counter_ids?: (string | number)[];
    status?: string;
    member_id?: string;
    erp_status?: string;
    type?: string;
  };

  // Applied filters (what actually gets sent to the API)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({});

  // Pending filters (what user is selecting but hasn't applied yet)
  const [pendingFilters, setPendingFilters] = useState<FilterState>({});

  // Filter modal state
  const [openFilter, setOpenFilter] = useState(false);

  const handleOpenFilter = (open: boolean | ((prev: boolean) => boolean)) => {
    const nextOpen = typeof open === "function" ? open(openFilter) : open;
    if (nextOpen) {
      setPendingFilters(appliedFilters);
    }
    setOpenFilter(nextOpen);
  };

  // Initial value for filters
  const initialValue = {
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "id",
  };

  // Build query for charts/summary (fetch more data for visualization)
  const buildSummaryQuery = () => {
    const params = new URLSearchParams();
    params.append("limit", "10000"); // Get more data for charts
    params.append("offset", "0");
    params.append("sort_order", "desc");
    params.append("load_summary", "true");
    params.append("sort_key", "id");

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        // Handle arrays (multiselect) - append each value separately
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for charts/summary (only when on charts tab)
  const { data: summaryData, isLoading: summaryLoading } = useGetSalesReportQuery(
    { query: buildSummaryQuery(), org_id: orgId },
    { skip: activeTab !== "charts" }
  );

  const chartData = React.useMemo(() => {
    return Array.isArray(summaryData?.data) ? summaryData?.data : [];
  }, [summaryData]);

  // Handle date range change (pending state only)
  const handleDateRange = (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => {
    const formattedStartDate = dates.start_date ? formatDate(dates.start_date) : "";
    const formattedEndDate = dates.end_date ? formatDate(dates.end_date) : "";

    const newFilters = {
      ...appliedFilters,
      start_date: formattedStartDate || undefined,
      end_date: formattedEndDate || undefined,
    };
    
    setPendingFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  // Apply filters - this is called when user clicks "Apply Filters"
  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setOpenFilter(false);
  };

  // Reset filters
  const resetFilters = () => {
    setPendingFilters({});
    setAppliedFilters({});
    setOpenFilter(false);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <div className="w-full p-5 space-y-6">
      {/* Shared Filters Component */}
      <SalesReportFilters
        dateRange={{
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
        }}
        appliedDateRange={{
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
        }}
        onDateRangeChange={handleDateRange}
        onResetFilters={resetFilters}
        onApplyFilters={applyFilters}
        filterData={pendingFilters}
        setFilterData={setPendingFilters}
        openFilter={openFilter}
        setOpenFilter={handleOpenFilter}
        initialValue={initialValue}
        showAdvancedFilters={true}
      />

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-fit grid-cols-2 mb-4">
          <TabsTrigger value="transactions">All Transactions</TabsTrigger>
          <TabsTrigger value="charts">Charts</TabsTrigger>
        </TabsList>

        <TabsContent value="charts" className="space-y-6">
          {/* Summary Cards */}
          <SalesSummary data={summaryData || { data: [], total_counts: 0, filtered_counts: 0 }} isLoading={summaryLoading} />

          {/* Full Charts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="fa-solid fa-chart-column text-green-600"></i>
                Sales Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SalesCharts
                data={chartData}
                isLoading={summaryLoading}
                filters={appliedFilters as any}
                dateRange={{
                  start_date: appliedFilters.start_date,
                  end_date: appliedFilters.end_date,
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-6">
          {/* Transaction Table - now handles its own API call with server-side pagination */}
          <SalesReportTable
            appliedFilters={appliedFilters}
          />

        </TabsContent>
      </Tabs>
    </div>
  );
}
